/**
 * The shared NDSS model vocabulary — SPL, assessment depth, evidence grading.
 *
 * PORTED VERBATIM from the full NDSS tool's `app/src/v2/engineConstants.ts`
 * (itself a mirror of the backend `ndss/engine/constants.py`, NDSS v3.8). The
 * Instant Scan is an extract of the same model, so these definitions must not
 * be re-invented here — if the full NDSS changes them, change them here too.
 *
 * NOTE ON BANDS vs SPL (decision 2026-07-19): the Instant Scan now leads with
 * the full NDSS's SPL level, superseding the neutral foundation/developing/
 * mature/leading wording that NDSS_Instant_Scan_Questions.md required. The
 * neutral `band` is deliberately KEPT in the data model: it is part of the
 * verified Power Automate contract and the SharePoint "Band" column. Changing
 * or removing it would break the live lead sink.
 */

export type SplLevel = 0 | 1 | 2 | 3 | 4;

export interface SplLevelDef {
  level: SplLevel;
  id: string;
  label: string;
  /** Inclusive bounds on the 0–100 score. */
  scoreMin: number;
  scoreMax: number;
  color: string;
}

/** Sovereignty Posture Level (concept §2.4). */
export const SPL_LEVELS: readonly SplLevelDef[] = [
  { level: 0, id: 'SPL-0', label: 'None', scoreMin: 0, scoreMax: 20, color: '#E42600' },
  { level: 1, id: 'SPL-1', label: 'Minimal', scoreMin: 21, scoreMax: 40, color: '#FF7A00' },
  { level: 2, id: 'SPL-2', label: 'Partial', scoreMin: 41, scoreMax: 60, color: '#FFC400' },
  { level: 3, id: 'SPL-3', label: 'Digital Resilience', scoreMin: 61, scoreMax: 79, color: '#19A3FC' },
  { level: 4, id: 'SPL-4', label: 'Digital Sovereignty', scoreMin: 80, scoreMax: 100, color: '#00CB5D' },
] as const;

export function splForScore(score: number): SplLevel {
  const s = Math.max(0, Math.min(100, score));
  for (const level of SPL_LEVELS) {
    if (s >= level.scoreMin && s <= level.scoreMax) return level.level;
  }
  return 4;
}

export function splDef(level: SplLevel): SplLevelDef {
  return SPL_LEVELS[level];
}

/**
 * Visitor-facing sentence for each SPL. Written for the Instant Scan: it must
 * read as an indicative position from self-declared answers, never a verdict.
 */
export const SPL_SENTENCE: Record<SplLevel, string> = {
  0: 'Sovereignty is largely unmanaged today — the exposure is broad rather than specific.',
  1: 'Early awareness, little structural control yet — most dimensions are still open.',
  2: 'Partial control: real foundations in some categories, material exposure in others.',
  3: 'Digital resilience: control is holding across most categories, with specific gaps left.',
  4: 'Digital sovereignty: strong, deliberate control across the board — remaining gaps are narrow.',
};

/* ------------------------------------------------------------------ *
 * Management summary — generic, keyed to the overall SPL.
 *
 * The full NDSS ships a summary written by the consultant who ran the
 * engagement. The Instant Scan cannot do that, but an empty section helps
 * nobody, so it carries one of five board-level texts chosen by SPL.
 *
 * These are DELIBERATELY GENERIC and the UI says so. They describe what a
 * posture level typically means and what usually moves it — never a claim
 * about this specific organisation, which would be invented.
 * ------------------------------------------------------------------ */

export const MANAGEMENT_SUMMARY: Record<SplLevel, string> = {
  0:
    'Your organisation currently operates with little structural control over its digital ' +
    'estate. Provider and platform choices, where data sits, and the ability to change course ' +
    'all sit largely outside your influence. At board level the exposure is not any single ' +
    'system — it is that there is no reliable answer to "what happens if we need to move, or ' +
    'if access is compelled". The first move is not a large programme: it is naming an ' +
    'accountable owner and establishing which systems would actually hurt to lose.',
  1:
    'Sovereignty is recognised here but not yet operational. The risks — jurisdiction, ' +
    'lock-in, dependency — are understood, without the contracts, architecture or tested ' +
    'processes that turn understanding into control. A board should assume that today most ' +
    'critical dependencies could not be exited or renegotiated at short notice. The ' +
    'highest-value next step is converting awareness into a small number of binding ' +
    'decisions: who owns this, which systems are in scope, and what evidence would satisfy you.',
  2:
    'Control is real but uneven. Some categories are genuinely managed while others carry ' +
    'material, addressable exposure — the most common posture we see, and the most ' +
    'improvable. The board question is no longer "are we exposed?" but "which exposures are ' +
    'we accepting deliberately?". At this level the gap between stated and demonstrable ' +
    'control is usually the main risk: much of what is believed to be true has never been ' +
    'verified.',
  3:
    'Your organisation holds working control across most dimensions and could absorb the loss ' +
    'or withdrawal of a major provider without existential disruption. What remains are ' +
    'specific, bounded gaps rather than systemic weakness. The value now shifts from building ' +
    'control to proving it — regulators, customers and boards increasingly ask for evidence ' +
    'rather than assertion. The usual next step is verification, plus closing the two or three ' +
    'categories that lag the rest.',
  4:
    'Your organisation demonstrates deliberate, exercised control over its critical digital ' +
    'estate. Decisions, data and dependencies are steerable, and exit is a tested capability ' +
    'rather than a contract clause. The remaining board concern is drift: sovereignty is a ' +
    'property of the architecture and the contracts you have today, and both change. The next ' +
    'step is periodic re-verification, and extending the same discipline to newer areas — ' +
    'typically AI supply chains and fourth-party dependencies.',
};

/** Shown directly beneath the summary. The invitation, not a hard sell. */
export const MANAGEMENT_SUMMARY_INVITATION =
  'This summary is generic to your posture level — it describes what this level usually means, ' +
  'not what is true of your organisation specifically. A Deep Scan replaces it with a narrative ' +
  'written against your estate, your regulatory context and the decisions already in flight, and ' +
  'reviewed with you before it is issued. If it would help to talk this through first, we are ' +
  'happy to spend 30 minutes on it — no preparation needed.';

/* ------------------------------------------------------------------ *
 * Assessment depth (concept §4.2) — the Instant Scan is L1.
 * ------------------------------------------------------------------ */

export interface DepthLevelDef {
  id: 'L1' | 'L2' | 'L3' | 'L4';
  rank: number;
  name: string;
  whatWeDo: string;
  /** Which Full-Stack Sovereignty Scan tier delivers this depth. */
  tier: string;
}

export const DEPTH_LEVELS: readonly DepthLevelDef[] = [
  { id: 'L1', rank: 1, name: 'Self-Assessment', whatWeDo: 'The customer answers online (self-declared).', tier: 'Instant Scan' },
  { id: 'L2', rank: 2, name: 'Snapshot', whatWeDo: 'We interview the responsible people.', tier: 'Quick Scan' },
  { id: 'L3', rank: 3, name: 'Extended', whatWeDo: "We review the customer's documentation.", tier: 'Deep Scan' },
  { id: 'L4', rank: 4, name: 'Fully Verified', whatWeDo: 'We verify it ourselves (inspection / test).', tier: 'Complete Scan' },
] as const;

/** The Instant Scan's depth. Everything in this tool is L1. */
export const INSTANT_SCAN_DEPTH = 'L1' as const;

/* ------------------------------------------------------------------ *
 * Evidence grading (concept §4.3) — the Instant Scan is E1 / C1.
 * ------------------------------------------------------------------ */

export interface EvidenceTypeDef {
  id: 'E1' | 'E2' | 'E3' | 'E4';
  rank: number;
  label: string;
  example: string;
}

export const EVIDENCE_TYPES: readonly EvidenceTypeDef[] = [
  { id: 'E1', rank: 1, label: 'Self-statement', example: 'questionnaire answer, verbal statement' },
  { id: 'E2', rank: 2, label: 'Documented', example: 'policy, concept, architecture diagram' },
  { id: 'E3', rank: 3, label: 'Direct inspection', example: 'config export, screenshot, live demo' },
  { id: 'E4', rank: 4, label: 'Technical proof', example: 'own test, scan result, reproduced sample' },
] as const;

export const CORROBORATION = [
  { id: 'C1', label: 'one source' },
  { id: 'C2', label: 'two or more independent sources' },
] as const;

export const INSTANT_SCAN_EVIDENCE = 'E1' as const;
export const INSTANT_SCAN_CORROBORATION = 'C1' as const;

/* ------------------------------------------------------------------ *
 * Workload criticality (concept §5.5). Not used at L1 — there is no
 * inventory — but named so the Scorecard can explain what it would mean.
 * ------------------------------------------------------------------ */

export const CRITICALITY_TIERS = [
  { id: 'high', label: 'Critical', weight: 3, ceiling: true },
  { id: 'medium', label: 'Important', weight: 2, ceiling: false },
  { id: 'low', label: 'Low', weight: 1, ceiling: false },
] as const;
